import type { SliderProps } from "@mui/material"
import {
  Box,
  Slider,
  Typography,
  darken,
  sliderClasses,
  styled,
  useTheme,
} from "@mui/material"
import type { ReactNode } from "react"
import { pathOr, stringToPath } from "remeda"
import type { RequireAllOrNone } from "type-fest"
import { formatPercentage } from "../../dataDisplay"
import { Flag } from "./Flag"

export type ProgressProps = Omit<SliderProps, "color" | "value"> & {
  value: number
  color?: string
  halfMarkColor?: string
  showPercentage?: boolean
  showEndMark?: boolean
  endLabel?: ReactNode
  endLabelPosition?: "after" | "end"
  showTooltip?: boolean
} & RequireAllOrNone<
    {
      referenceValue: number
      referenceLabel: ReactNode
    },
    "referenceLabel" | "referenceValue"
  >

export const Progress = ({
  value,
  referenceValue,
  referenceLabel,
  endLabel,
  showPercentage = false,
  showEndMark = false,
  halfMarkColor,
  color = "primary.main",
  endLabelPosition = "end",
  showTooltip = false,
  ...props
}: ProgressProps) => {
  const { palette } = useTheme()
  const finalColor = pathOr(palette, stringToPath(color), color) as string
  const darkColor = darken(finalColor, 0.3)
  return (
    <Box display="flex" alignItems="center" gap={2}>
      <Box position="relative" flex={1} lineHeight={0}>
        {/* BAR */}
        <ProgressBar
          {...props}
          value={value}
          trackColor={finalColor}
          thumbColor={darkColor}
        />

        {/* HALF MARK */}
        {!!halfMarkColor && <HalfMark bgcolor={halfMarkColor} />}

        {/* END MARK */}
        {!!showEndMark && value !== 100 && <EndMark bgcolor={finalColor} />}

        {/* END LABEL */}
        {!!endLabel && endLabelPosition === "end" && (
          <EndLabelEndPos>{endLabel}</EndLabelEndPos>
        )}

        {/* PERCENTAGE LABEL */}
        {!!showPercentage && value !== 0 && (
          <PercentageLabel
            color={darkColor}
            bgcolor={halfMarkColor}
            percentage={value}
          >
            {formatPercentage(value, {
              roundingMode: "floor",
              maximumFractionDigits: 0,
            })}
          </PercentageLabel>
        )}

        {/* REFERENCE VALUE */}
        {!!referenceValue && (
          <ReferenceMark color={finalColor} percentage={referenceValue}>
            <div style={{ position: "relative" }}>
              <ReferenceMarkIcon>
                <Flag color={finalColor} />
              </ReferenceMarkIcon>
            </div>
          </ReferenceMark>
        )}
      </Box>
      {!!endLabel && endLabelPosition === "after" && <EndLabel>{endLabel}</EndLabel>}
    </Box>
  )
}

const BAR_HEIGHT = 8

const ProgressBar = styled(Slider, {
  shouldForwardProp: (prop) => prop !== "trackColor" && prop !== "thumbColor",
})<{ trackColor: string; thumbColor: string }>(({ trackColor, thumbColor }) => ({
  height: BAR_HEIGHT,

  [`& .${sliderClasses.rail}`]: {
    backgroundColor: "#cfd4d9",
    height: "6px",
  },
  [`& .${sliderClasses.track}`]: {
    backgroundColor: trackColor,
  },
  [`& .${sliderClasses.thumb}`]: {
    backgroundColor: thumbColor,
    zIndex: 5,
  },
}))
ProgressBar.defaultProps = {
  disabled: true,
  size: "small",
}

const HalfMark = styled(Box)({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "2px",
  height: BAR_HEIGHT,
  zIndex: 0,
})

const EndMark = styled(Box)({
  position: "absolute",
  top: "50%",
  right: -2,
  transform: "translate(-50%, -50%)",
  width: "2px",
  height: 12,
  zIndex: 0,
})

const EndLabel = styled(Typography)(({ theme: { palette, typography } }) => ({
  fontSize: typography.pxToRem(12),
  lineHeight: typography.pxToRem(12),
  color: palette.grey.light,
}))

const EndLabelEndPos = styled(EndLabel)(() => ({
  position: "absolute",
  top: "-10px",
  right: 0,
  transform: "translateY(-50%)",
  zIndex: 0,
}))

const PercentageLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "percentage",
})<{ percentage: number }>(({ theme: { typography }, percentage }) => ({
  position: "absolute",
  top: "calc(50% - 7px)",
  left: `${percentage}%`,
  transform: "translate(-50%, 100%)",
  fontSize: typography.pxToRem(14),
  lineHeight: typography.pxToRem(14),
  fontWeight: 600,
  textAlign: "center",
  padding: "1px 2px",
  zIndex: 2,
}))

const REFERENCE_MARK_SIZE = 8
const ReferenceMark = styled("div", {
  shouldForwardProp: (prop) => prop !== "percentage",
})<{ percentage: number }>(({ color, percentage, theme }) => ({
  position: "absolute",
  top: "50%",
  left: `${percentage}%`,
  transform: "translate(-50%, -50%)",
  width: REFERENCE_MARK_SIZE,
  height: REFERENCE_MARK_SIZE,
  backgroundColor: color,
  border: `1px solid ${theme.palette.common.americanDiamond}`,
  borderRadius: "100px",
  zIndex: 1,
}))

const ReferenceMarkIcon = styled("div")(() => ({
  position: "absolute",
  top: "-30px",
  left: "calc(50% - 1px)",
}))
